import { firmAPI } from './api';

export interface FirmRegistrationData {
  name: string;
  type: 'client' | 'vendor';
  address_1: string;
  address_2?: string;
  zip_code: string;
  state: string;
  city: string;
  country: string;
  contact_phone?: string;
  website?: string;
  is_authorized_signatory: boolean;
  registration_data?: {
    full_name: string;
    email: string;
    job_title?: string;
  };
}

export interface Firm {
  id: number;
  name: string;
  type: 'client' | 'vendor';
  category?: 'prepaid' | 'postpaid';
  address_1: string;
  address_2?: string;
  zip_code: string;
  state: string;
  city: string;
  country: string;
  currency?: string;
  contact_phone?: string;
  website?: string;
  is_authorized_signatory: boolean;
  status: 'pending' | 'approved' | 'rejected';
  approved_by?: number;
  approved_at?: string;
  registration_data?: any;
  created_at: string;
  updated_at: string;
}

export interface FirmListResponse {
  success: boolean;
  data: {
    data: Firm[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const firmService = {
  // Register a new firm (public endpoint)
  registerFirm: async (data: FirmRegistrationData): Promise<any> => {
    try {
      const response = await firmAPI.register(data);
      return response.data;
    } catch (error: any) {
      // For validation errors (422), preserve the full error structure
      if (error.response?.status === 422) {
        throw error;
      }
      // For other errors, throw a generic message
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to register firm');
    }
  },

  // Get all firms (admin only)
  getAllFirms: async (params?: any): Promise<FirmListResponse> => {
    try {
      const response = await firmAPI.getAll(params);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch firms');
    }
  },

  // Get firm by ID
  getFirmById: async (id: number): Promise<{ success: boolean; data: Firm }> => {
    try {
      const response = await firmAPI.getById(id);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch firm');
    }
  },

  // Create firm (admin only)
  createFirm: async (data: any): Promise<any> => {
    try {
      const response = await firmAPI.create(data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to create firm');
    }
  },

  // Update firm
  updateFirm: async (id: number, data: any): Promise<any> => {
    try {
      const response = await firmAPI.update(id, data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update firm');
    }
  },

  // Delete firm (admin only)
  deleteFirm: async (id: number): Promise<any> => {
    try {
      const response = await firmAPI.delete(id);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to delete firm');
    }
  },

  // Approve firm (admin only)
  approveFirm: async (id: number, type?: 'client' | 'vendor', customAgreementId?: number): Promise<any> => {
    try {
      const payload: any = type ? { type } : {};
      if (customAgreementId) {
        payload.custom_agreement_id = customAgreementId;
      }
      const response = await firmAPI.approve(id, payload);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to approve firm');
    }
  },

  // Reject firm (admin only)
  rejectFirm: async (id: number, reason?: string): Promise<any> => {
    try {
      const response = await firmAPI.reject(id, reason ? { reason } : {});
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to reject firm');
    }
  },
};
